'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import { Button } from '@/components/ui/forms/button';
import { Label } from '@/components/ui/forms/label';
import {
  ACCEPTED_PROFILE_PHOTO_FILE_EXTENSIONS,
  ACCEPTED_PROFILE_PHOTO_MIME_TYPES,
  PROFILE_PHOTO_FORMATS_HUMAN_LABEL,
  PROFILE_PHOTO_MAX_SIZE_HUMAN_LABEL,
  validateProfilePhotoFile,
} from '@/lib/resume/profile-picture';
import {
  clampPhotoCropTransform,
  getPhotoCropRect,
  getPhotoPreviewStyle,
  IDENTITY_PHOTO_CROP_TRANSFORM,
  MAX_PHOTO_ZOOM,
  MIN_PHOTO_ZOOM,
  PHOTO_ZOOM_STEP,
  PROFILE_PHOTO_OUTPUT_SIZE,
  type PhotoCropTransform,
} from '@/lib/resume/photo-crop';

/** Side of the square crop stage, in CSS pixels. */
const VIEWPORT_SIZE = 288;

/**
 * Output is always JPEG: it is the format every PDF renderer and Word processor
 * handles without argument, and a cropped headshot has nothing to gain from
 * alpha. Transparent source pixels are composited onto white first, so a PNG
 * avatar does not arrive as a black square.
 */
const OUTPUT_MIME_TYPE = 'image/jpeg';
const OUTPUT_QUALITY = 0.9;

interface ProfilePictureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Receives the cropped square; resolves once the caller has stored it. */
  onConfirm: (blob: Blob) => Promise<void>;
  /**
   * The photo already on the resume. When present the dialog opens with it
   * loaded and ready to reposition, instead of an empty "choose an image"
   * state — re-framing an existing photo is the common case once one is set.
   * Choosing a new file still replaces it.
   */
  initialImageUrl?: string | null;
}

interface LoadedImage {
  element: HTMLImageElement;
  /** What the preview renders. A blob URL for a picked file, a plain URL for the saved photo. */
  src: string;
  /** Only blob URLs need revoking; revoking the saved photo's URL would be wrong. */
  isObjectUrl: boolean;
}

/**
 * Pick, frame and crop a profile photo.
 *
 * The crop happens here on a canvas rather than server-side because the app has
 * no image-processing dependency (no `sharp`), and because uploading the
 * finished square means what is stored is exactly what the user framed — no
 * second interpretation of the crop box at render time.
 */
export function ProfilePictureDialog({
  open,
  onOpenChange,
  onConfirm,
  initialImageUrl,
}: ProfilePictureDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [image, setImage] = useState<LoadedImage | null>(null);
  const [transform, setTransform] = useState<PhotoCropTransform>(IDENTITY_PHOTO_CROP_TRANSFORM);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dragOrigin = useRef<{ x: number; y: number; transform: PhotoCropTransform } | null>(null);

  // Object URLs are never collected on their own, and the dialog can be closed
  // or re-picked at any point. One cleanup covers both: it fires when `image`
  // is replaced and again on unmount.
  useEffect(() => {
    if (!image?.isObjectUrl) return; // A plain URL has nothing to revoke.
    const { src } = image;
    return () => URL.revokeObjectURL(src);
  }, [image]);

  const resetState = useCallback(() => {
    setImage(null);
    setTransform(IDENTITY_PHOTO_CROP_TRANSFORM);
    setError(null);
    setSaving(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    if (!initialImageUrl) return;

    // Preload the photo already on the resume so the dialog opens ready to
    // reposition. `crossOrigin` matters: the crop draws to a canvas and calls
    // toBlob, and a cross-origin image without CORS taints it, making that
    // throw. Our own photo route is same-origin; a legacy external URL may not
    // cooperate, so a failure here degrades to the empty picker rather than
    // blocking the dialog.
    let cancelled = false;
    const element = new Image();
    element.crossOrigin = 'anonymous';
    element.onload = () => {
      if (cancelled) return;
      setImage({ element, src: initialImageUrl, isObjectUrl: false });
      setTransform(IDENTITY_PHOTO_CROP_TRANSFORM);
    };
    element.onerror = () => {
      if (cancelled) return;
      setImage(null);
      setError('Your current photo could not be loaded for editing. Choose an image to replace it.');
    };
    element.src = initialImageUrl;

    return () => {
      cancelled = true;
    };
  }, [open, initialImageUrl, resetState]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Allow re-picking the same file after a rejection.
    event.target.value = '';
    if (!file) return;

    const validation = validateProfilePhotoFile(file);
    if (!validation.ok) {
      setError(validation.message);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const element = new Image();
    element.onload = () => {
      setImage({ element, src: objectUrl, isObjectUrl: true });
      setTransform(IDENTITY_PHOTO_CROP_TRANSFORM);
      setError(null);
    };
    element.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setError('That image could not be read. Try a different file.');
    };
    element.src = objectUrl;
  };

  const cropInput = image
    ? {
        naturalWidth: image.element.naturalWidth,
        naturalHeight: image.element.naturalHeight,
        viewportSize: VIEWPORT_SIZE,
        transform,
      }
    : null;

  const previewStyle = cropInput ? getPhotoPreviewStyle(cropInput) : null;

  const applyTransform = (next: PhotoCropTransform) => {
    if (!cropInput) return;
    setTransform(clampPhotoCropTransform({ ...cropInput, transform: next }));
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!image) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragOrigin.current = { x: event.clientX, y: event.clientY, transform };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const origin = dragOrigin.current;
    if (!origin) return;
    applyTransform({
      zoom: origin.transform.zoom,
      offsetX: origin.transform.offsetX + (event.clientX - origin.x),
      offsetY: origin.transform.offsetY + (event.clientY - origin.y),
    });
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    dragOrigin.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleConfirm = async () => {
    if (!image || !cropInput) return;

    const rect = getPhotoCropRect(cropInput);
    if (rect.sourceSize <= 0) {
      setError('That image could not be cropped. Try a different file.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = PROFILE_PHOTO_OUTPUT_SIZE;
    canvas.height = PROFILE_PHOTO_OUTPUT_SIZE;
    const context = canvas.getContext('2d');
    if (!context) {
      setError('Your browser could not process this image.');
      return;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, PROFILE_PHOTO_OUTPUT_SIZE, PROFILE_PHOTO_OUTPUT_SIZE);
    context.drawImage(
      image.element,
      rect.sourceX,
      rect.sourceY,
      rect.sourceSize,
      rect.sourceSize,
      0,
      0,
      PROFILE_PHOTO_OUTPUT_SIZE,
      PROFILE_PHOTO_OUTPUT_SIZE
    );

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, OUTPUT_MIME_TYPE, OUTPUT_QUALITY);
    });

    if (!blob) {
      setError('Your browser could not process this image.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onConfirm(blob);
      onOpenChange(false);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Upload failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={saving ? undefined : onOpenChange}>
      <DialogContent>
        <DialogHeading
          title="Profile Photo"
          description="Drag to reposition and zoom to frame the crop. The square you see is what gets saved."
        />

        <div className="space-y-4 py-2">
          <div
            className="relative mx-auto overflow-hidden rounded-full border border-border bg-muted"
            style={{ width: VIEWPORT_SIZE, height: VIEWPORT_SIZE, touchAction: 'none' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            {image && previewStyle ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={image.src}
                alt=""
                draggable={false}
                className="absolute left-1/2 top-1/2 max-w-none select-none"
                style={{
                  width: previewStyle.width,
                  height: previewStyle.height,
                  transform: `translate(calc(-50% + ${previewStyle.translateX}px), calc(-50% + ${previewStyle.translateY}px))`,
                  cursor: 'grab',
                }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center px-6 text-center text-caption text-muted-foreground">
                Choose an image to start framing it
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="profilePhotoZoom">Zoom</Label>
            <input
              id="profilePhotoZoom"
              type="range"
              className="w-full accent-primary disabled:opacity-50"
              min={MIN_PHOTO_ZOOM}
              max={MAX_PHOTO_ZOOM}
              step={PHOTO_ZOOM_STEP}
              value={transform.zoom}
              disabled={!image}
              onChange={(event) => applyTransform({ ...transform, zoom: Number(event.target.value) })}
            />
          </div>

          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={saving}>
              {image ? 'Choose a different image' : 'Choose image'}
            </Button>
            <span className="text-caption text-muted-foreground">
              {PROFILE_PHOTO_FORMATS_HUMAN_LABEL}, {PROFILE_PHOTO_MAX_SIZE_HUMAN_LABEL}
            </span>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={`${ACCEPTED_PROFILE_PHOTO_MIME_TYPES.join(',')},${ACCEPTED_PROFILE_PHOTO_FILE_EXTENSIONS}`}
            onChange={handleFileChange}
          />

          {error && <p className="text-caption text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!image || saving}>
            {saving ? 'Saving…' : 'Save photo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
